// Known non-actionable noise from browser extensions, embedded webviews and third-party scripts -
// none of it originates in our code, so it is filtered at `Sentry.init` rather than triaged.
//
// Ported from the sibling ui-support app, which arrived at this list the hard way. Entries carry the
// ui-myaccount issue they were added for where one exists; the rest are marked as inherited.
//
// Do not `Fixes <SENTRY-SHORT-ID>` an issue that was only suppressed here - that wrongly auto-closes
// it as resolved. Resolve or mute those issues manually in Sentry instead.
export const sentryIgnoreErrors = [
  // A third-party XHR failing and rejecting with the DOM error Event rather than an Error, which
  // Sentry serialises with no stack and no attributable frames. There is no XMLHttpRequest,
  // EventSource or WebSocket anywhere in src/ - we use fetch throughout - so these cannot be ours.
  // UI-MYACCOUNT-8Z (667 events / 69 users) and UI-MYACCOUNT-95 (329 / 88).
  'Event `XMLHttpRequestProgressEvent` (type=error) captured as promise rejection',
  'Event `ProgressEvent` (type=error) captured as promise rejection',
  // Inherited from ui-support: browser-extension content scripts, embedded webviews and Safari
  // private-mode storage restrictions. No ui-myaccount occurrences yet, kept because none of these
  // can originate in our code and each cost ui-support real volume before it was filtered.
  'Object Not Found Matching Id',
  'Invalid call to runtime.sendMessage',
  "Can't find variable: localStorage",
  'Access is denied for this document',
  'getLayoutMap() must be called',
  'WKWebView API client did not respond',
  // A third-party script rejecting a promise with a plain object rather than an Error - Sentry has
  // no frames to attribute these to, so they can only be matched on this message text
  // (`ignoreErrors` does a substring match). `[object has no keys]` is Sentry's own wording for an
  // empty-object rejection. We never reject a promise with a plain object anywhere in src/.
  'Object captured as promise rejection with keys: [object has no keys]',
  'Object captured as promise rejection with keys: details, message, status'
];

export const sentryDenyUrls = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
  /^safari-web-extension:\/\//
];

/** Minimal shape of the pieces of a Sentry event these predicates inspect. */
type FilterableEvent = {
  message?: string;
  exception?: {
    values?: { value?: string; mechanism?: { type?: string } }[];
  };
};

function firstExceptionValue(event: FilterableEvent) {
  return event.exception?.values?.[0];
}

function combinedMessage(event: FilterableEvent, originalException?: unknown) {
  const message = event.message || String(originalException ?? '');
  return `${message} ${firstExceptionValue(event)?.value ?? ''}`;
}

/**
 * `CustomEvent` unhandled-rejection noise: a rejected promise whose value is a DOM CustomEvent
 * rather than an Error, which Sentry serialises with no useful stack (UI-MYACCOUNT-V, 193 events /
 * 29 users). We never reject a promise with a CustomEvent.
 */
export function isCustomEventRejection(event: FilterableEvent, originalException?: unknown): boolean {
  const message = combinedMessage(event, originalException);
  return (
    message.includes('CustomEvent') &&
    message.includes('unhandledrejection') &&
    message.includes('captured as promise rejection')
  );
}

/** The exact mechanism Sentry tags the wrapped iframe `load` handler with. */
const REPLAY_IFRAME_CRASH_MECHANISM = 'auto.browser.browserapierrors.addEventListener';

/**
 * The Session Replay SDK crashing on a cross-origin iframe (UI-MYACCOUNT-3Q, 166 events / 4 users:
 * `SecurityError: Failed to read a named property 'Element' from 'Window': Blocked a frame with
 * origin "https://www.parcel2go.com" from accessing a cross-origin frame`).
 *
 * `onIframeLoad` calls `observeAttachShadow`, which guards `iframeDoc` and `iframeWindow` but not
 * `iframeWindow.Element` - reading that off a cross-origin frame either throws a `SecurityError`
 * synchronously or, when the engine lets the read through as `undefined`, crashes one line later in
 * `patchAttachShadow` on `.prototype` of that undefined value. Same root cause, two exception shapes
 * depending on how far the SDK gets. It is pure SDK self-noise: there is no `<iframe>` anywhere in
 * src/, so these come from third-party tags.
 *
 * Matched narrowly on the message AND the SDK's own instrumentation mechanism, so an ordinary
 * application error is never swallowed.
 */
export function isSentryReplayIframeCrash(event: FilterableEvent): boolean {
  const exception = firstExceptionValue(event);
  const message = exception?.value ?? event.message ?? '';
  // V8: "Cannot read properties of undefined (reading 'prototype')"
  // WebKit: "undefined is not an object (evaluating 'e.prototype')"
  const readsPrototypeOfUndefined =
    message.includes("reading 'prototype'") ||
    (message.includes('undefined is not an object') && message.includes('prototype'));
  // Chrome, when it throws instead of resolving to undefined.
  const blockedCrossOriginFrameRead = message.includes('accessing a cross-origin frame');

  return (
    (readsPrototypeOfUndefined || blockedCrossOriginFrameRead) &&
    exception?.mechanism?.type === REPLAY_IFRAME_CRASH_MECHANISM
  );
}

/** Sentry tags a crash inside any wrapped `XMLHttpRequest` event handler with this prefix. */
const XHR_HANDLER_MECHANISM_PREFIX = 'auto.browser.browserapierrors.xhr.';

/**
 * A crash inside a wrapped `XMLHttpRequest` event handler (`onload`, `onerror`, etc.). There is no
 * `XMLHttpRequest` anywhere in src/ - we use `fetch` throughout - so anything Sentry attributes to
 * this instrumentation mechanism cannot be ours, regardless of the specific message.
 *
 * Deliberately matched on the mechanism rather than a message string: in ui-support a
 * message-scoped version of this same predicate shipped first and immediately missed a second
 * message variant of the identical third-party bug.
 */
export function isThirdPartyXhrFailure(event: FilterableEvent): boolean {
  const mechanism = firstExceptionValue(event)?.mechanism?.type;
  return typeof mechanism === 'string' && mechanism.startsWith(XHR_HANDLER_MECHANISM_PREFIX);
}
